import { ThemeProvider, useMediaQuery } from '@material-ui/core'
import { Loading } from 'components/Loading/Loading'
import { parse } from 'query-string'
import React, { lazy, Suspense, useEffect, useState } from 'react'
import { BrowserRouter, Redirect, Route, Switch, useLocation } from 'react-router-dom'
import { ToastContainer } from 'react-toastify'
import { Settings } from 'types'
import config from '../../config'
import { SessionState } from '../../reducers/session'
import theme from '../../theme'
import { getLocalStorage, trackPageView } from '../../util'
import ForgotPassword from '../Login/ForgotPassword'
import ResetPassword from '../Login/ResetPassword'
import { RootLogin } from '../Login/RootLogin'
import { Auth0Login } from '../Login/Auth0Login'
import { AuthOIDCHandler } from '../Login/AuthOIDCHandler'
import { I18nProvider } from '../../v3/context/i18n'
import { fetchPlatformSettings } from '../../request/fetchPlatformSettings'
import { OneClickAction } from '../OneClickAction/OneClickAction'
import { getSessionFromLocalStorage, getUserThemeFromSession } from '../../util/session'
import { RecaptchaProvider } from '../../v3/context/recaptcha'
import { useAuth } from '../../hooks/useAuth'

import { isSessionValid } from '../../util/session'
import { resetDateFilters, setLocalStorage } from '../../util'
import { useHistory } from 'react-router-dom'
import { login } from 'actions'

const MobileRoot = lazy(() => import('../Mobile/App'))
const V1Root = lazy(() => import('../AppV1/Wrapper'))

/**
 * The AppRoot is the entry-point for Command Center
 * It handles top-level routing, session fetching, and redirection
 */
export default function AppRoot() {
  // Dark mode is not supported on Mobile (under 600px)
  const mobileView = useMediaQuery('(max-width:600px)')
  const preferDark = useMediaQuery('(prefers-color-scheme: dark)')

  // SAVE_USER_SUCCESS and RootLogin will emit a 'toggle-theme' event on the window when a user saves their
  // own account info - this listener picks up the theme change and the state
  // variable will trigger the useEffect hook..
  const [themeReload, setThemeReload] = useState(false)

  useEffect(() => {
    const toggleTheme = () => {
      setThemeReload((prev) => !prev)
    }
    window.addEventListener('toggle-theme', toggleTheme)

    return () => {
      window.removeEventListener('toggle-theme', toggleTheme)
    }
  }, [])

  const [userTheme, setUserTheme] = useState<string>(preferDark ? 'dark' : 'light')

  // Switch to dark theme when the user does not have a preference and their
  // OS switches.
  useEffect(() => {
    let newTheme = 'light'
    if (!mobileView) {
      newTheme = preferDark ? 'dark' : 'light'
      const userTheme = getUserThemeFromSession(getSessionFromLocalStorage())
      if (userTheme) {
        newTheme = userTheme
      }
    }

    setUserTheme((prevState) => {
      if (prevState === newTheme) {
        return prevState
      }
      return newTheme
    })
  }, [themeReload, preferDark, mobileView])

  let locale: string | null = getLocalStorage(config.languageKey)
  if (!locale) {
    locale = 'en-US'
  }

  return (
    <ThemeProvider theme={theme(userTheme)}>
      <span className={userTheme}>
        <I18nProvider locale={locale}>
          <RecaptchaProvider>
            <BrowserRouter>
              <Switch>
                <Route path="/forgot-password">
                  <ForgotPassword />
                  <PageChangeTracker />
                </Route>

                <Route path="/my-account/reset-password">
                  <ResetPasswordRoute />
                  <PageChangeTracker />
                </Route>

                <Route path="/auth/auth0">
                  <Auth0LoginRoute />
                  <PageChangeTracker />
                </Route>

                <Route path="/auth/callback">
                  <Auth0CallbackRoute />
                  <PageChangeTracker />
                </Route>

                <Route path="/login">
                  <LoginRoute />
                  <PageChangeTracker />
                </Route>

                <Route path="/auth/:orgId/oidc">
                  <AuthOIDCRoute />
                </Route>

                <Route exact path="/oca/lead/:leadID/:result?">
                  <OneClickAction />
                  <PageChangeTracker />
                </Route>

                <Route>
                  <DefaultAppRoute />
                </Route>
              </Switch>
            </BrowserRouter>
          </RecaptchaProvider>
        </I18nProvider>
        <ToastContainer />
      </span>
    </ThemeProvider>
  )
}

// Exists so that page loads for routes defined by react-router can trigger pageview events in PC Tracking
// Legacy redux-first-router routed page views are tracked automatically in the App component
function PageChangeTracker() {
  const location = useLocation()

  useEffect(() => {
    trackPageView()
  }, [location])
  return null
}

function ResetPasswordRoute() {
  const params: { token?: string } = parse(window.location.search)
  return <ResetPassword token={params.token} />
}

function Auth0LoginRoute() {
  const { loginWithRedirect } = useAuth()

  useEffect(() => {
    loginWithRedirect()
  }, [loginWithRedirect])

  return null
}

function Auth0CallbackRoute() {
  return <Auth0Login />
}

function AuthOIDCRoute() {
  return <AuthOIDCHandler />
}

function LoginRoute() {
  // If authorized, redirect to root '/'. the respective routers, redux-router or react-router depending on app view, will redirect accordingly
  const session: SessionState | null = getSessionFromLocalStorage()
  if (session) {
    return <Redirect to="/" />
  }
  return <RootLogin />
}

// Installed via <script> tag in index.html
declare const pendo: {
  initialize: (settings: {}) => void
}

function DefaultAppRoute() {
  const [platformSettings, setPlatformSettings] = useState<Settings>()
  const width = window.innerWidth
  const location = useLocation()

  const session: SessionState | null = getSessionFromLocalStorage()

  useEffect(() => {
    if (!session) return
    if (platformSettings) return
    fetchPlatformSettings(session).then((s) => setPlatformSettings(s))
  }, [platformSettings, session])

  if (!session) {
    return (
      <Redirect
        to={{
          pathname: '/login',
          state: { from: location },
        }}
      />
    )
  }

  // Pendo install docs: https://app.eu.pendo.io/setup?setupKey=eyJTdWJzY3JpcHRpb25OYW1lIjoiUG93ZXJDaG9yZCIsIkhtYWMiOiJ6ZDI3TzFhaUdiT3AxN2x1YWdVZjJ0SnRYRWlIR3JTV3V6dXYwVDFwVWRJPSJ9&os=web
  pendo.initialize({
    visitor: {
      id: session.user.ID,
      email: session.user.email,
      full_name: session.user.fullName,
    },
    account: {
      id: session.organization.ID,
      name: session.organization.name,
    },
  })

  if (!platformSettings) {
    return <Loading fullScreen={true} />
  }

  const shouldShowMobile = width < 600 && platformSettings?.enableV3
  if (shouldShowMobile) {
    return (
      <Suspense fallback={<Loading fullScreen={true} />}>
        <MobileRoot session={session} platformSettings={platformSettings} />
      </Suspense>
    )
  }
  return (
    <Suspense fallback={<Loading fullScreen={true} />}>
      <V1Root session={session} platformSettings={platformSettings} />
    </Suspense>
  )
}
